import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  FiUserCheck,
  FiArrowLeft,
  FiBook,
  FiLayers,
  FiUsers,
  FiDollarSign,
} from "react-icons/fi";
import Button from "../../components/Button/Button";
import adminApi from "../../api/adminApi";
import FormInput from "../../components/FormInput/FormInput";
import Select from "../../components/Select/Select";
import "./CreateEnrollment.css";

const CreateEnrollment = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [formData, setFormData] = useState({
    user: "",
    batch: "",
    course: "",
    notes: "",
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (formData.course) {
      loadBatchesForCourse(formData.course);
    } else {
      // Load all batches if no course is selected
      loadAllBatches();
    }
  }, [formData.course]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadStudents(), loadCourses()]);
      await loadAllBatches();
    } catch (err) {
      setError("Failed to load initial data");
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      const response = await adminApi.get("/users", {
        params: { role: "student", limit: 1000 },
      });
      const usersList = response.data?.users || response.data?.data || [];
      setStudents(usersList);
    } catch (err) {
      console.error("Failed to load students:", err);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await adminApi.get("/courses", {
        params: { limit: 1000 },
      });
      const coursesList = response.data?.courses || response.data?.data || [];
      setCourses(coursesList);
    } catch (err) {
      console.error("Failed to load courses:", err);
    }
  };

  const loadAllBatches = async () => {
    try {
      const response = await adminApi.get("/batches", {
        params: { limit: 1000 },
      });
      const batchesList = response.data?.batches || response.data?.data || [];
      setBatches(batchesList);
    } catch (err) {
      console.error("Failed to load batches:", err);
    }
  };

  const loadBatchesForCourse = async (courseId) => {
    try {
      const response = await adminApi.get("/batches", {
        params: { courseId, limit: 1000 },
      });
      const batchesList = response.data?.batches || response.data?.data || [];
      setBatches(batchesList);
      const course = courses.find((c) => c._id === courseId);
      if (course) {
        setSelectedCourse(course);
      }
    } catch (err) {
      console.error("Failed to load batches:", err);
    }
  };

  const handleBatchChange = (batchId) => {
    const batch = batches.find((b) => b._id === batchId);
    setSelectedBatch(batch || null);
    setFormData({ ...formData, batch: batchId });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    if (!formData.user || !formData.batch) {
      setError("Please select both student and batch");
      setSubmitting(false);
      return;
    }

    try {
      const enrollmentData = {
        user: formData.user,
        batch: formData.batch,
      };

      if (formData.course) {
        enrollmentData.course = formData.course;
      }

      const response = await adminApi.post("/enrollments", enrollmentData);

      // If notes were provided, update the enrollment with notes
      if (formData.notes) {
        try {
          await adminApi.patch(`/enrollments/${response.data.enrollment?._id || response.data._id}`, {
            notes: formData.notes,
          });
        } catch (err) {
          console.error("Failed to add notes:", err);
        }
      }

      navigate(`/enrollments/${response.data.enrollment?._id || response.data._id}`);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to create enrollment"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="create-loading">Loading...</div>;
  }

  return (
    <div className="create-enrollment-page">
      <div className="page-header">
        <div>
          <Button
            variant="secondary"
            size="small"
            onClick={() => navigate("/enrollments")}
          >
            <FiArrowLeft size={16} />
            Back to Enrollments
          </Button>
          <h1>
            <FiUserCheck size={24} />
            Create New Enrollment
          </h1>
          <p>Enroll a student in a batch</p>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="form-container">
        <form onSubmit={handleSubmit} className="enrollment-form">
          <div className="form-section">
            <h2>Student Selection</h2>
            <Select
              label="Student"
              name="user"
              value={formData.user}
              onChange={(e) => setFormData({ ...formData, user: e.target.value })}
              options={[
                { value: "", label: "Select a student" },
                ...students.map((s) => ({
                  value: s._id,
                  label: `${s.firstName} ${s.lastName}`.trim() || s.email,
                })),
              ]}
              required
            />
            {formData.user && (
              <div className="info-card">
                <h3>
                  <FiUsers size={18} />
                  Student Information
                </h3>
                {(() => {
                  const student = students.find((s) => s._id === formData.user);
                  return student ? (
                    <>
                      <p>
                        <strong>Name:</strong> {student.firstName} {student.lastName}
                      </p>
                      <p>
                        <strong>Email:</strong> {student.email}
                      </p>
                      {student.phone && (
                        <p>
                          <strong>Phone:</strong> {student.phone}
                        </p>
                      )}
                      {student.selectedCourse && (
                        <p>
                          <strong>Selected Course:</strong>{" "}
                          {courses.find((c) => c._id === student.selectedCourse)
                            ?.title || "N/A"}
                        </p>
                      )}
                    </>
                  ) : null;
                })()}
              </div>
            )}
          </div>

          <div className="form-section">
            <h2>Batch Selection</h2>
            <Select
              label="Course (Optional - for payment verification)"
              name="course"
              value={formData.course}
              onChange={(e) => {
                setFormData({ ...formData, course: e.target.value, batch: "" });
                setSelectedBatch(null);
              }}
              options={[
                { value: "", label: "Select a course (optional)" },
                ...courses.map((c) => ({ value: c._id, label: c.title })),
              ]}
            />

            <Select
              label="Batch"
              name="batch"
              value={formData.batch}
              onChange={(e) => handleBatchChange(e.target.value)}
              options={[
                { value: "", label: "Select a batch" },
                ...batches
                  .filter(
                    (b) =>
                      b.status === "upcoming" || b.status === "ongoing"
                  )
                  .map((b) => ({
                    value: b._id,
                    label: `${b.batchName} (${
                      b.course && typeof b.course === "object" && b.course.title
                        ? b.course.title
                        : b.status
                    })`,
                  })),
              ]}
              required
            />

            {selectedBatch && (
              <div className="info-card">
                <h3>
                  <FiLayers size={18} />
                  Batch Information
                </h3>
                <p>
                  <strong>Batch:</strong> {selectedBatch.batchName}
                </p>
                {selectedBatch.course && (
                  <p>
                    <strong>Course:</strong>{" "}
                    <Link to={`/courses/${selectedBatch.course._id || selectedBatch.course}`}>
                      {selectedBatch.course.title || "N/A"}
                    </Link>
                  </p>
                )}
                <p>
                  <strong>Status:</strong> {selectedBatch.status}
                </p>
                {selectedBatch.startDate && (
                  <p>
                    <strong>Start Date:</strong>{" "}
                    {new Date(selectedBatch.startDate).toLocaleDateString()}
                  </p>
                )}
                {selectedBatch.endDate && (
                  <p>
                    <strong>End Date:</strong>{" "}
                    {new Date(selectedBatch.endDate).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="form-section">
            <h2>Additional Information</h2>
            <FormInput
              label="Notes (Optional)"
              name="notes"
              type="textarea"
              rows={4}
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Add any notes or comments about this enrollment..."
            />
          </div>

          <div className="form-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate("/enrollments")}
            >
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>
              Create Enrollment
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEnrollment;

